let _ = t => t,
  _t;
/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { EERIE, LORAX, LORAX_DARK } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import Small from 'UIComponents/elements/Small';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UIHelpLink from 'UIComponents/tooltip/UIHelpLink';
import * as PurchaseMotionsLocations from 'pricing-pages-lib/constants/PurchaseMotionsLocations';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getOfferIdFromIdByUnitScopeRecord } from 'pricing-pages-lib/utils/offerUtils';
import { getProductVersion } from 'pricing-pages-lib/utils/productsUtils';
import breakpoint from 'pricing-pages-ui/constants/Breakpoints';
import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ENTERPRISE } from 'self-service-api/constants/ProductLevels';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import styled from 'styled-components';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { INTERSTITIAL } from '../../constants/tracking/Locations';
import { PlanBuilderContext } from '../../context/PlanBuilderContext';
import { useAppSelector } from '../../reducers';
import { activeCurrencySelector, availableCurrenciesSelector } from '../../selectors/CurrencySelectors';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { commonAvailableTermsSelector, commonPaymentTermsSelector, offerInfoSelector } from '../../selectors/OfferSelectors';
import { calculatedPriceSelector, planAdditionalCostSelector, priceBreakdownSelector } from '../../selectors/PricingSelectors';
import { offerProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { crmSuiteOwnershipLevel, hasPlanBuilderProductsInBundledProductInventorySelector, hasProductVersionInProductInventorySelector, hasSkuInProductInventorySelector, ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelSelector, productForSkuIdSelector, productVersionSelector, skuIdsContainOnlyCrmSuite, skuSelector } from '../../selectors/ProductsSelectors';
import { isHSFXEligibleSelector } from '../../selectors/PromotionSelectors';
import { commonPurchaseMotionsSelector, suitePurchaseMotionsMapSelector } from '../../selectors/PurchaseMotionSelectors';
import { requiredOnboardingChoiceSkuIdsSelector, requiredOnboardingSkuIdsSelector } from '../../selectors/RequiredOnboardingSelectors';
import { productDataSelector } from '../../selectors/SelectorUtils';
import classNames from '../../utils/classNames';
import CurrencyPicker from '../common/CurrencyPicker';
import CurrentPlanTag from '../common/CurrentPlanTag';
import DetailedTermSelector from '../common/DetailedTermSelector';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import AsyncPricingBreakdownModal from '../common/pricing/AsyncPricingBreakdownModal';
import CrmSuiteAlert from './CrmSuiteAlert';
import EmptyShoppingCart from './EmptyShoppingCart';
import PlanBuilderCTAsContainer from './PlanBuilderCTAsContainer';
import { PriceDisplaySection } from './PriceDisplaySection';
import RequiredOnboardingSubtextContainer from './RequiredOnboardingSubtextContainer';
import ShareLink from './ShareLink';
import ShoppingCart from './ShoppingCart';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledSummaryColumnWrapper = styled(UICardWrapper).withConfig({
  displayName: "SummaryColumn__StyledSummaryColumnWrapper",
  componentId: "sc-8rskhp-0"
})(["overflow:hidden;"]);
const StyledGradientBar = styled.div.withConfig({
  displayName: "SummaryColumn__StyledGradientBar",
  componentId: "sc-8rskhp-1"
})(["height:3px;background-color:", ";background-image:linear-gradient( -303deg,", ",0%,", " 40%,", " 100% );"], LORAX_DARK, LORAX, LORAX, LORAX_DARK);
const PriceBreakdownLink = styled(UILink).withConfig({
  displayName: "SummaryColumn__PriceBreakdownLink",
  componentId: "sc-8rskhp-2"
})(["margin-top:36px;", ";"], breakpoint.tablet(_t || (_t = _`
    font-size: 0.875rem;
  `)));
const RequiredOnboardingWrapper = styled.div.withConfig({
  displayName: "SummaryColumn__RequiredOnboardingWrapper",
  componentId: "sc-8rskhp-3"
})(["text-align:center;&:not(:empty){margin-top:12px;}"]);
const StyledSubtextHelpLink = styled(UIHelpLink).withConfig({
  displayName: "SummaryColumn__StyledSubtextHelpLink",
  componentId: "sc-8rskhp-4"
})(["color:", ";"], EERIE);
const SummaryColumn = ({
  className,
  shareLinkAnchor,
  hideCrmSuiteAlert = false,
  hideCurrencyPicker = true,
  hubsSignupProductOverride,
  overrideHideTermSelector = false,
  ctaOverrideComponent = null,
  ctaOverrideProps = {},
  allowRemoveCartItems = true
}) => {
  const {
    queryParamTerm,
    setQueryParamTerm,
    productConfigurations,
    setProductConfigurations,
    requiredProductLines,
    checkoutProductConfigurations,
    trackingSource,
    cartDisplayProductConfigurations,
    breakdownDisplayProductConfigurations
  } = useContext(PlanBuilderContext);
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const availableCurrencies = useAppSelector(availableCurrenciesSelector);
  const shouldShowCurrencyContainer = !hideCurrencyPicker && availableCurrencies && availableCurrencies.length > 1;
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isHSFX = useSelector(isHSFXEligibleSelector);
  const skuIds = useMemo(() => productConfigurations.map(({
    skuId
  }) => skuId), [productConfigurations]);
  const productNames = useAppSelector(state => skuIds.map(skuId => productForSkuIdSelector(state, {
    skuId
  }).apiName));
  const requiredSkuIds = useAppSelector(state => skuIds.filter(skuId => {
    const product = productForSkuIdSelector(state, {
      skuId
    });
    return product.productLevel && requiredProductLines.includes(product.productLine);
  }));
  const [isPriceBreakdownVisible, setIsPriceBreakdownVisible] = useState(false);
  const activeCurrencyCode = useSelector(activeCurrencySelector);
  const suiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const hasOnlySuiteSkuIds = useSelector(state => skuIdsContainOnlyCrmSuite(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const selectedOwnedSuiteProductLevel = useSelector(state => {
    if (!suiteProductLevel) return false;
    return ownsCrmSuiteProductLevel(state, {
      productLevel: suiteProductLevel
    });
  });
  const price = useAppSelector(state => suiteProductLevel && !selectedOwnedSuiteProductLevel ? calculatedPriceSelector(state, {
    productConfigurations: breakdownDisplayProductConfigurations,
    queryParamTerm
  }) : planAdditionalCostSelector(state, {
    productConfigurations: breakdownDisplayProductConfigurations,
    queryParamTerm
  }));
  const ownsAnyBaseHubs = useAppSelector(state => checkoutProductConfigurations.some(({
    skuId
  }) => {
    return hasProductVersionInProductInventorySelector(state, {
      skuId: productVersionSelector(state, {
        skuId
      }).skuId
    });
  }));
  const priceBreakdown = useAppSelector(state => priceBreakdownSelector(state, {
    productConfigurations: breakdownDisplayProductConfigurations,
    queryParamTerm,
    adjustPricesForOwnership: true
  }));
  const hasMultipleTerms = useSelector(state => commonAvailableTermsSelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }).length > 1);
  const showTermSelector = overrideHideTermSelector ? false : hasMultipleTerms;
  const paymentTerm = useAppSelector(state => {
    const offerProductConfigurations = offerProductConfigurationsSelector(state, {
      productConfigurations,
      queryParamTerm
    });
    const {
      offerId: firstAvailableOfferId
    } = offerProductConfigurations.find(({
      offerId: offerIdInConfig
    }) => {
      return offerIdInConfig;
    }) || {
      offerId: null
    };

    // This should always pass when `offerProductConfigurations` is non-empty.
    const areAllPaymentTermsSame = offerProductConfigurations.every(({
      offerId: offerIdInConfig
    }) => offerInfoSelector(state, {
      offerId: getOfferIdFromIdByUnitScopeRecord(offerIdInConfig)
    }).payment === offerInfoSelector(state, {
      offerId: getOfferIdFromIdByUnitScopeRecord(firstAvailableOfferId)
    }).payment);
    const offerIdToUse = areAllPaymentTermsSame ? firstAvailableOfferId : null;

    // If all offer ids have the same payment term, return that payment term
    if (offerIdToUse) return offerInfoSelector(state, {
      offerId: getOfferIdFromIdByUnitScopeRecord(offerIdToUse)
    }).payment;

    // Returns the lowest common payment term if query param term is invalid
    const commonPaymentTerms = commonPaymentTermsSelector(state, {
      skuIds: productConfigurations.map(({
        skuId
      }) => skuId)
    });
    if (!commonPaymentTerms || !commonPaymentTerms.length) return MONTHLY;
    if (!commonPaymentTerms.includes(queryParamTerm)) return commonPaymentTerms[0];
    return queryParamTerm;
  });
  const productData = useSelector(productDataSelector);
  const onRemove = skuIdToRemove => {
    setProductConfigurations(() => {
      return productConfigurations.filter(({
        skuId
      }) => {
        return getProductVersion({
          productData
        }, {
          skuId: skuIdToRemove
        }).skuId !== getProductVersion({
          productData
        }, {
          skuId
        }).skuId;
      });
    });
  };
  const onboardingChoiceSkuIds = useAppSelector(state => skuIds.map(skuId => requiredOnboardingChoiceSkuIdsSelector(state, {
    skuId
  })).flat().filter(skuId => !!skuId));
  const hasOnboardingChoices = onboardingChoiceSkuIds && !!onboardingChoiceSkuIds.length;
  const hasRequiredOnboarding = useAppSelector(state => skuIds.some(skuId => requiredOnboardingSkuIdsSelector(state, {
    skuId
  })));

  //If there's required onboarding, it's auto included in the cost
  //If there's choice onboarding, check if any choices have been added to the
  // breakdown display product configs already
  const includesOnboardingCost = hasRequiredOnboarding || breakdownDisplayProductConfigurations.some(config => onboardingChoiceSkuIds.includes(config.skuId));
  const purchaseMotions = useAppSelector(state => {
    if (suiteProductLevel && hasOnlySuiteSkuIds && suiteProductLevel !== ENTERPRISE) {
      const suitePurchaseMotionsMap = suitePurchaseMotionsMapSelector(state);
      if (suitePurchaseMotionsMap) {
        return suitePurchaseMotionsMap[suiteProductLevel];
      } else return [];
    }
    // This helps cover the case when we are just buying additional contacts / users
    const ownsAllItemsInCart = checkoutProductConfigurations.every(({
      skuId
    }) => hasSkuInProductInventorySelector(state, {
      skuId
    }));
    const skuIdsToUse = checkoutProductConfigurations.map(({
      skuId
    }) => skuId).filter(skuId => {
      const isQuantifiable = skuSelector(state, {
        skuId
      }).quantifiable;
      return isQuantifiable || !hasSkuInProductInventorySelector(state, {
        skuId
      }) || ownsAllItemsInCart;
    });
    const commonPurchaseMotions = commonPurchaseMotionsSelector(state, {
      skuIds: skuIdsToUse,
      location: PurchaseMotionsLocations.CALCULATOR
    });

    // HSFX-eligible products and starter products don't have any common terms,
    // so we cannot send them to checkout touchlessly.
    if (isHSFX) {
      const hasCommonTerms = !!commonAvailableTermsSelector(state, {
        skuIds: skuIdsToUse
      }).length;
      if (!hasCommonTerms) {
        return commonPurchaseMotions.filter(purchaseMotion => purchaseMotion !== BUY_TOUCHLESS);
      }
    }
    return commonPurchaseMotions;
  });
  const ownsProductViaBundle = useAppSelector(state => hasPricingAndPurchasingSucceeded && hasPlanBuilderProductsInBundledProductInventorySelector(state, {
    skuIds
  }));
  const suiteOwnershipLevel = useAppSelector(state => hasPricingAndPurchasingSucceeded && crmSuiteOwnershipLevel(state));
  if (!checkoutProductConfigurations.length) {
    return /*#__PURE__*/_jsxs(StyledSummaryColumnWrapper, {
      className: className,
      flush: true,
      children: [/*#__PURE__*/_jsx(StyledGradientBar, {}), /*#__PURE__*/_jsx(EmptyShoppingCart, {})]
    });
  }
  const isCheckingOutAnyProducts = checkoutProductConfigurations.length && checkoutProductConfigurations.some(({
    quantity
  }) => quantity > 0);
  const handlePriceBreakdownLinkClick = () => {
    getUsageTracker().track('clickToViewModal', {
      name: 'pricing-breakdown-modal',
      location: trackingSource,
      productNames,
      skuIds
    });
    setIsPriceBreakdownVisible(true);
  };
  const PriceDisplay = () => /*#__PURE__*/_jsx("div", {
    className: classNames('text-center', isUngatedForSeats ? 'm-top-10' : 'm-bottom-10'),
    children: /*#__PURE__*/_jsx(PriceDisplaySection, {
      monthlyCostPrice: price.monthly.calculatedPrice,
      annualListPrice: price.annually.fullPrice,
      activeCurrencyCode: activeCurrencyCode,
      annualCostPrice: price.annually.calculatedPrice,
      term: paymentTerm,
      costToGetStarted: price.oneTimeFee.calculatedPrice + (paymentTerm === ANNUAL ? price.annually.calculatedPrice : price.monthly.calculatedPrice),
      showTotalPriceAsterisk: hasOnboardingChoices || hasRequiredOnboarding,
      showingOnlyAdditionalCost: ownsAnyBaseHubs && !suiteProductLevel || selectedOwnedSuiteProductLevel
    })
  });
  const CTAContainer = ctaOverrideComponent || PlanBuilderCTAsContainer;
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(StyledSummaryColumnWrapper, {
      className: className,
      flush: true,
      children: [/*#__PURE__*/_jsx(StyledGradientBar, {}), ownsProductViaBundle && /*#__PURE__*/_jsx(CurrentPlanTag, {
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `pricingPage.highlightedMerchCards.youOwnSuite.${suiteOwnershipLevel}`
        }),
        tagStyle: {
          marginTop: '3px'
        }
      }), /*#__PURE__*/_jsxs("div", {
        className: "p-x-8 p-y-6 flex-column",
        children: [shouldShowCurrencyContainer && /*#__PURE__*/_jsxs(UIFlex, {
          direction: "row",
          justify: "center",
          align: "center",
          children: [/*#__PURE__*/_jsx("div", {
            style: {
              fontWeight: 600,
              marginBottom: 3
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "interstitial.currency"
            })
          }), /*#__PURE__*/_jsx(CurrencyPicker, {})]
        }), showTermSelector && /*#__PURE__*/_jsx("div", {
          className: classNames(isUngatedForSeats ? 'm-bottom-10' : 'm-bottom-4'),
          children: /*#__PURE__*/_jsx(DetailedTermSelector, {
            trackingSource: trackingSource,
            activeTerm: queryParamTerm,
            onChange: setQueryParamTerm,
            skuIds: productConfigurations.map(({
              skuId
            }) => skuId)
          })
        }), !isUngatedForSeats && /*#__PURE__*/_jsx(PriceDisplay, {}), /*#__PURE__*/_jsx(ShoppingCart, {
          productConfigurations: selectedOwnedSuiteProductLevel ? cartDisplayProductConfigurations : productConfigurations,
          onRemove: onRemove,
          requiredSkuIds: requiredSkuIds,
          queryParamTerm: queryParamTerm,
          allowRemoveCartItems: allowRemoveCartItems,
          showSeatStepper: trackingSource !== INTERSTITIAL
        }), isUngatedForSeats && /*#__PURE__*/_jsx(PriceDisplay, {}), /*#__PURE__*/_jsx("div", {
          className: "flex-column align-center justify-center m-top-10 m-x-0",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            Content: CTAContainer,
            contentProps: Object.assign({
              productConfigurations: checkoutProductConfigurations,
              queryParamTerm,
              ctaStyles: {
                display: 'block',
                minWidth: 200,
                width: '100%',
                lineHeight: '24px',
                paddingTop: 16
              },
              containterStyle: {
                paddingTop: 0
              },
              trackingSource,
              hubsSignupProductOverride,
              purchaseMotions
            }, ctaOverrideProps)
          })
        }), !hideCrmSuiteAlert && /*#__PURE__*/_jsx(CrmSuiteAlert, {
          suiteProductLevel: suiteProductLevel
        }), isCheckingOutAnyProducts && /*#__PURE__*/_jsx("div", {
          className: "justify-center",
          children: /*#__PURE__*/_jsx(PriceBreakdownLink, {
            onClick: handlePriceBreakdownLinkClick,
            onMouseEnter: () => AsyncPricingBreakdownModal.preload(),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "planBuilder.viewPriceBreakdownLink"
            })
          })
        }), shareLinkAnchor !== undefined && isCheckingOutAnyProducts && /*#__PURE__*/_jsx("div", {
          className: "justify-center m-top-4",
          children: /*#__PURE__*/_jsx(ShareLink, {
            anchorId: shareLinkAnchor,
            trackingSource: trackingSource
          })
        }), /*#__PURE__*/_jsx(RequiredOnboardingWrapper, {
          children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
            queryParamTerm: queryParamTerm,
            skuIds: skuIds,
            trackingSource: trackingSource,
            includesOnboardingCost: includesOnboardingCost
          })
        })]
      }), isPriceBreakdownVisible && /*#__PURE__*/_jsx(AsyncPricingBreakdownModal, {
        onCloseClick: () => setIsPriceBreakdownVisible(false),
        priceBreakdown: priceBreakdown,
        skuIds: skuIds,
        paymentTerm: paymentTerm
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: "m-top-2 text-center",
      children: /*#__PURE__*/_jsx(StyledSubtextHelpLink, {
        content: {
          body: /*#__PURE__*/_jsxs("div", {
            className: "p-all-5",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.feeIncreasesProductServicesCatalog"
            }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.annualDiscountLegalDisclaimer"
            })]
          })
        },
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.priceDisclaimer"
          })
        })
      })
    })]
  });
};
export default SummaryColumn;